import { Layout, Model, Actions, DockLocation } from "flexlayout-react"
import { Stage, Layer, Rect, Circle, Text, Group } from "react-konva"
import { useState, useRef, useEffect } from "react"
import "flexlayout-react/style/rounded.css"
import "./styles.css"

const initialJson = {
    global: {},
    borders: [
        {
            type: "border",
            location: "left",
            size: 200,
            children: [
                {
                    type: "tab",
                    name: "Menü",
                    component: "menu",
                    enableClose: false,
                }
            ]
        }
    ],
    layout: {
        type: "row",
        weight: 100,
        children: [
            {
                type: "tabset",
                weight: 100,
                id: "main_tabset",
                children: [
                    { type: "tab", name: "Szenario 1", component: "placeholder" }
                ]
            }
        ]
    }
}

const CIRCLE_R = 28

const scenarioRects = [
    {
        id: "rect-1",
        x: 420,
        y: 50,
        width: 240,
        height: 140,
        label: "Bund",
        level: 1,
        color: "#2563eb",
    },
    {
        id: "rect-2",
        x: 50,
        y: 360,
        width: 980,
        height: 430,
        label: "Thüringen",
        level: 2,
        color: "#16a34a",
    },
    {
        id: "rect-3",
        x: 200,
        y: 540,
        width: 680,
        height: 190,
        label: "Thüringer Kommunen",
        level: 3,
        color: "#f59e0b",
    },
]

const initialScenarioCircles = [
    { id: "circle-1", x: 420, y: 255, label: "Sach-\naufgabe" },
    { id: "circle-2", x: 540, y: 255, label: "IT-\nAufgabe" },
    { id: "circle-3", x: 660, y: 255, label: "Finanz-\nverantwortung" },
]

function distance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y)
}

function getCircleLabelStyle(label) {
    const lineCount = label.split("\n").length
    if (lineCount === 1) {
        return { x: -22, y: -8, width: 44, fontSize: 10 }
    }
    if (lineCount === 2) {
        return { x: -24, y: -12, width: 48, fontSize: 10 }
    }
    return { x: -26, y: -16, width: 52, fontSize: 7 }
}

function ScenarioCanvas() {
    const [circles, setCircles] = useState(initialScenarioCircles)
    const [stageScale, setStageScale] = useState(1)
    const [stagePos, setStagePos] = useState({ x: 0, y: 0 })
    const [stageSize, setStageSize] = useState({ width: 900, height: 500 })
    const [activeOverlaps, setActiveOverlaps] = useState({})
    const containerRef = useRef(null)
    const activeRectIds = new Set(Object.values(activeOverlaps).flat())

    const getOverlappingRectIds = (x, y) => {
        const overlappingRects = scenarioRects.filter((rect) => {
            const nearestX = Math.max(rect.x, Math.min(x, rect.x + rect.width))
            const nearestY = Math.max(rect.y, Math.min(y, rect.y + rect.height))
            const d = distance({ x, y }, { x: nearestX, y: nearestY })
            return d < CIRCLE_R
        })

        if (overlappingRects.length === 0) return []

        const highestLevel = Math.max(...overlappingRects.map((rect) => rect.level))

        return overlappingRects
            .filter((rect) => rect.level === highestLevel)
            .map((rect) => rect.id)
    }

    const updateOverlapState = (circleId, x, y) => {
        const overlappingRectIds = getOverlappingRectIds(x, y)

        setActiveOverlaps((prev) => {
            if (overlappingRectIds.length === 0) {
                const next = { ...prev }
                delete next[circleId]
                return next
            }

            return {
                ...prev,
                [circleId]: overlappingRectIds,
            }
        })
    }

    const handleDragEnd = (circleId, evt) => {
        const { x, y } = evt.target.position()

        setCircles((prev) =>
            prev.map((c) => {
                if (c.id !== circleId) return c
                return { ...c, x, y }
            })
        )

        updateOverlapState(circleId, x, y)
    }

    const handleWheel = (e) => {
        e.evt.preventDefault()

        const scaleBy = 1.05
        const stage = e.target.getStage()
        const oldScale = stage.scaleX()
        const pointer = stage.getPointerPosition()

        if (!pointer) return

        const mousePointTo = {
            x: (pointer.x - stage.x()) / oldScale,
            y: (pointer.y - stage.y()) / oldScale,
        }

        const direction = e.evt.deltaY > 0 ? -1 : 1
        const newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy

        setStageScale(newScale)
        setStagePos({
            x: pointer.x - mousePointTo.x * newScale,
            y: pointer.y - mousePointTo.y * newScale,
        })
    }

    useEffect(() => {
        const element = containerRef.current
        if (!element) return

        const updateSize = () => {
            const width = element.clientWidth || 900
            const height = element.clientHeight || 500

            setStageSize({ width, height })

            // center content (approx bounding box of your layout)
            const contentWidth = 1080
            const contentHeight = 820

            setStagePos({
                x: (width - contentWidth) / 2,
                y: (height - contentHeight) / 2,
            })
        }

        updateSize()

        const observer = new ResizeObserver(() => {
            updateSize()
        })

        observer.observe(element)

        return () => {
            observer.disconnect()
        }
    }, [])

    return (
        <div ref={containerRef} style={{ width: "100%", height: "100%", minHeight: 400 }}>
            <Stage
                width={stageSize.width}
                height={stageSize.height}
                scaleX={stageScale}
                scaleY={stageScale}
                x={stagePos.x}
                y={stagePos.y}
                draggable
                dragButtons={[1]}
                onWheel={handleWheel}
                style={{ border: "1px solid #ccc", background: "#fafafa" }}
            >
                <Layer>
                    {scenarioRects.map((r) => {
                        const isTouching = activeRectIds.has(r.id)

                        return (
                            <Group key={r.id}>
                                <Rect
                                    x={r.x}
                                    y={r.y}
                                    width={r.width}
                                    height={r.height}
                                    cornerRadius={12}
                                    stroke={isTouching ? r.color : "#333"}
                                    strokeWidth={isTouching ? 4 : 2}
                                    fill="#f2f2f2"
                                />
                                <Text
                                    x={r.x}
                                    y={r.y + 26}
                                    width={r.width}
                                    align="center"
                                    text={r.label}
                                    fontSize={r.label === "Kommune" ? 20 : 18}
                                    fill="#222"
                                />
                            </Group>
                        )
                    })}

                    {circles.map((c) => {
                        const overlappingRectIds = activeOverlaps[c.id] || []
                        const activeRect = scenarioRects.find((r) => r.id === overlappingRectIds[0])
                        const isTouching = Boolean(activeRect)
                        const labelStyle = getCircleLabelStyle(c.label)

                        return (
                            <Group
                                key={c.id}
                                x={c.x}
                                y={c.y}
                                draggable
                                onDragMove={(evt) => {
                                    const { x, y } = evt.target.position()
                                    updateOverlapState(c.id, x, y)
                                }}
                                onDragEnd={(evt) => handleDragEnd(c.id, evt)}
                            >
                                <Circle
                                    x={0}
                                    y={0}
                                    radius={CIRCLE_R}
                                    fill="#dbeafe"
                                    stroke={isTouching ? activeRect.color : "#333"}
                                    strokeWidth={isTouching ? 4 : 2}
                                />
                                <Text
                                    x={labelStyle.x}
                                    y={labelStyle.y}
                                    width={labelStyle.width}
                                    align="center"
                                    text={c.label}
                                    fontSize={labelStyle.fontSize}
                                    lineHeight={1.05}
                                    fill="#111"
                                />
                            </Group>
                        )
                    })}
                </Layer>
            </Stage>
        </div>
    )
}

export default function App() {
    const [model] = useState(() => Model.fromJson(initialJson))
    const [, setTick] = useState(0)
    const nextTabId = useRef(2)

    useEffect(() => {
        const listener = () => setTick(t => t + 1)
        model.addChangeListener(listener)
        return () => model.removeChangeListener(listener)
    }, [model])

    const addTab = (targetId, location) => {
        model.doAction(Actions.addNode(
            { type: "tab", component: "placeholder", name: `Szenario ${nextTabId.current++}` },
            targetId, location, location === DockLocation.CENTER ? -1 : 0
        ))
    }

    const onRenderTabSet = (node, renderValues) => {
        if (node.getType() !== "tabset") return
        renderValues.stickyButtons.push(
            <button key="add-button" title="Add New Tab" className="flexlayout__tab_toolbar_button-custom"
                    onClick={() => addTab(node.getId(), DockLocation.CENTER)}>
                +
            </button>
        )
    }

    const factory = (node) => {
        const component = node.getComponent()
        if (component === "placeholder") {
            return <ScenarioCanvas />
        }
        if (component === "menu") {
            const activeTabset = model.getActiveTabset()
            const targetId = activeTabset ? activeTabset.getId() : "main_tabset"
            const rootId = model.getRoot().getId()
            const disabled = !activeTabset && !model.getNodeById("main_tabset")
            // {activeTabset ? `Target: ${activeTabset.getName() || activeTabset.getId()}` : "Select a tabset first"}
            return (
                <div>
                    <div>Use Case:</div>
                    <strong>KFZ-Zulassung</strong>
                    <br/><br/>
                    <button disabled={disabled} onClick={() => addTab(targetId, DockLocation.BOTTOM)}>
                        split selected row
                    </button>
                    <button disabled={disabled} onClick={() => addTab(targetId, DockLocation.RIGHT)}>
                        split selected column
                    </button>
                    <button onClick={() => addTab(rootId, DockLocation.BOTTOM)}>
                        add new row
                    </button>
                    <button onClick={() => addTab(rootId, DockLocation.RIGHT)}>
                        add new column
                    </button>
                </div>
            )
        }
    }

    return <Layout model={model} factory={factory} onRenderTabSet={onRenderTabSet} />
}
